import { rollups, sum, groups, stack, stackOrderNone, stackOffsetNone } from 'd3';
import type { TradeValueScaledByYAxisOption } from '../types';

/** Scaled trade values for a single direction pair */
export interface ScaledTradeValue {
  exportValue: number;
  importValue: number;
}

/** A transformed data item with trade value scaling structure */
export interface TransformedTradeDataItem {
  year: number;
  productId?: string;
  partnerId?: string;
  topLevelParent?: string;
  exportValue?: number;
  importValue?: number;
  [scaledTradeValueKey: string]: unknown;
}

/** A year-grouped stack input datum */
export interface StackInputDatum {
  year: number;
  [entityId: string]: unknown;
}

export interface BuildStackDataParams {
  transformedData: TransformedTradeDataItem[];
  yearMin: number;
  yearMax: number;
  entityIdAccessor: (d: TransformedTradeDataItem) => string | undefined;
  scaledTradeValueSelector: TradeValueScaledByYAxisOption;
  tradeDirectionSelector: string;
  stackOrderFunction: typeof stackOrderNone;
  stackOffsetFunction: typeof stackOffsetNone;
  setYAxisDomain: (domain: { min: number; max: number }) => void;
}

export const buildStackData = ({
  transformedData,
  yearMin,
  yearMax,
  entityIdAccessor,
  scaledTradeValueSelector,
  tradeDirectionSelector,
  stackOrderFunction,
  stackOffsetFunction,
  setYAxisDomain,
}: BuildStackDataParams) => {
  const filteredDataWithinYearLimits = transformedData.filter(
    (d: TransformedTradeDataItem) => d.year >= yearMin && d.year <= yearMax,
  );

  // Compute the max value for the y-scale
  const sumTradeValueByYear = rollups(
    filteredDataWithinYearLimits,
    (d: TransformedTradeDataItem[]) =>
      sum(
        d,
        (v: TransformedTradeDataItem) =>
          (v[scaledTradeValueSelector] as Record<string, number>)?.[tradeDirectionSelector] ?? 0,
      ),
    (d: TransformedTradeDataItem) => d.year,
  ).map((d) => d[1]);
  const maxTradeValue = Math.max(...sumTradeValueByYear);

  if (maxTradeValue) {
    setYAxisDomain({ min: 0, max: maxTradeValue });
  }

  // Build stack input data grouped by year → entity
  const stackInputData: StackInputDatum[] = [];
  groups(
    filteredDataWithinYearLimits,
    ({ year }) => year,
    (d) => entityIdAccessor(d),
  ).map((yearGrouping) => {
    const year = yearGrouping[0];
    const entities = yearGrouping[1];

    const yearDatum: StackInputDatum = { year: year as number };

    entities.forEach(([entityId, [datum]]: [string, TransformedTradeDataItem[]]) => {
      const { exportValue, importValue } =
        datum[scaledTradeValueSelector] as ScaledTradeValue;
      (yearDatum as Record<string, unknown>)[entityId as string] = {};
      ((yearDatum as Record<string, unknown>)[entityId as string] as Record<string, unknown>)[scaledTradeValueSelector] = {
        exportValue,
        importValue,
      };
    });

    stackInputData.push(yearDatum);
  });

  const stackKeysSet = new Set(
    filteredDataWithinYearLimits.map((d: TransformedTradeDataItem) => entityIdAccessor(d)!),
  );

  const sortedStackInputData = [...stackInputData].sort(
    (a: StackInputDatum, b: StackInputDatum) => a.year - b.year,
  );

  const stackGenerator = stack()
    .keys([...stackKeysSet])
    .value((d: Record<string, unknown>, key: string) =>
      d.hasOwnProperty(key)
        ? ((d[key] as Record<string, Record<string, number>>)?.[scaledTradeValueSelector]?.[tradeDirectionSelector] ?? 0)
        : 0,
    )
    .order(stackOrderFunction)
    .offset(stackOffsetFunction);

  const series = stackGenerator(sortedStackInputData as Iterable<{ [key: string]: number }>);

  return { series, stackKeysSet };
};
